import type { CalendarEvent, Occurrence } from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'

/**
 * Escritas da agenda: compromisso avulso e remarcação de ocorrência.
 *
 * Leitura de compromisso NUNCA passa por aqui — ela vem da view
 * `calendar_event_visible`, em `core-api.ts`, que é quem aplica a visibilidade
 * (R7/R8). Esta tabela base só recebe escrita, e o `select` depois do insert
 * só devolve linha de quem já podia vê-la inteira.
 */

export type AgendaWriteResult<T> = { ok: true; data: T } | { ok: false; reason: string }

interface EventRow {
  id: string
  title: string
  description: string | null
  location: string | null
  category_id: string | null
  date: string
  time_of_day: string | null
  duration_minutes: number
  visibility: CalendarEvent['visibility']
  owner_id: string
}

function dbError(error: { message?: string } | null, fallback: string): string {
  return describeDbError(error, fallback)
}

function toEvent(row: EventRow, participantIds: readonly string[]): CalendarEvent {
  return {
    id: row.id,
    title: row.title,
    date: row.date,
    durationMinutes: row.duration_minutes,
    visibility: row.visibility,
    ownerId: row.owner_id,
    // Quem acabou de gravar é dono: vê tudo, por definição.
    canSeeDetail: true,
    participantIds: [...participantIds],
    ...(row.time_of_day ? { time: row.time_of_day.slice(0, 5) } : {}),
    ...(row.description ? { description: row.description } : {}),
    ...(row.location ? { location: row.location } : {}),
    ...(row.category_id ? { categoryId: row.category_id } : {}),
  }
}

export interface EventInput {
  title: string
  description: string | null
  location: string | null
  categoryId: string | null
  date: string
  /** `HH:MM`, ou null para dia inteiro. */
  time: string | null
  durationMinutes: number
  visibility: CalendarEvent['visibility']
  ownerId: string
  participantIds: readonly string[]
}

function payloadOf(input: EventInput): Record<string, unknown> {
  return {
    title: input.title.trim(),
    description: input.description?.trim() || null,
    location: input.location?.trim() || null,
    category_id: input.categoryId,
    date: input.date,
    time_of_day: input.time,
    duration_minutes: input.durationMinutes,
    visibility: input.visibility,
    owner_id: input.ownerId,
  }
}

export async function insertEvent(
  input: EventInput,
  createdBy: string,
): Promise<AgendaWriteResult<CalendarEvent>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('calendar_event')
    .insert({ ...payloadOf(input), created_by: createdBy })
    .select('*')
    .single()

  if (error || !data) {
    return {
      ok: false,
      reason: dbError(error, 'Não foi possível marcar o compromisso.'),
    }
  }

  const row = data as EventRow
  const participants = await replaceEventParticipants(row.id, input.participantIds)
  if (!participants.ok) {
    return {
      ok: false,
      reason: `Compromisso marcado, mas os participantes não foram salvos: ${participants.reason}`,
    }
  }
  return { ok: true, data: toEvent(row, participants.data) }
}

export async function patchEvent(
  id: string,
  input: EventInput,
): Promise<AgendaWriteResult<CalendarEvent>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('calendar_event')
    .update(payloadOf(input))
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return {
      ok: false,
      reason: dbError(error, 'Não foi possível salvar o compromisso.'),
    }
  }

  const participants = await replaceEventParticipants(id, input.participantIds)
  if (!participants.ok) {
    return {
      ok: false,
      reason: `Compromisso salvo, mas os participantes não foram atualizados: ${participants.reason}`,
    }
  }
  return { ok: true, data: toEvent(data as EventRow, participants.data) }
}

/** Remarcar: só a data e a hora mudam. O resto do compromisso fica. */
export async function rescheduleEvent(
  id: string,
  date: string,
  time: string | null,
): Promise<AgendaWriteResult<{ date: string; time: string | null }>> {
  const sb = getSupabase()
  const { error } = await sb
    .from('calendar_event')
    .update({ date, time_of_day: time })
    .eq('id', id)

  if (error) {
    return {
      ok: false,
      reason: dbError(error, 'Não foi possível remarcar o compromisso.'),
    }
  }
  return { ok: true, data: { date, time } }
}

export async function deleteEventRemote(id: string): Promise<AgendaWriteResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.from('calendar_event').delete().eq('id', id)
  if (error) {
    return {
      ok: false,
      reason: dbError(error, 'Não foi possível excluir o compromisso.'),
    }
  }
  return { ok: true, data: true }
}

export async function replaceEventParticipants(
  eventId: string,
  memberIds: readonly string[],
): Promise<AgendaWriteResult<string[]>> {
  const sb = getSupabase()
  const next = [...new Set(memberIds)]

  const { data: existing, error: readError } = await sb
    .from('calendar_event_participant')
    .select('member_id')
    .eq('event_id', eventId)

  if (readError) {
    return {
      ok: false,
      reason: dbError(readError, 'Falha ao ler os participantes atuais.'),
    }
  }

  const current = ((existing ?? []) as { member_id: string }[]).map((r) => r.member_id)
  const currentSet = new Set(current)
  const nextSet = new Set(next)
  const toRemove = current.filter((id) => !nextSet.has(id))
  const toAdd = next.filter((id) => !currentSet.has(id))

  if (toRemove.length > 0) {
    const { error } = await sb
      .from('calendar_event_participant')
      .delete()
      .eq('event_id', eventId)
      .in('member_id', toRemove)
    if (error) {
      return {
        ok: false,
        reason: dbError(error, 'Falha ao remover participantes.'),
      }
    }
  }

  if (toAdd.length > 0) {
    const { error } = await sb
      .from('calendar_event_participant')
      .insert(toAdd.map((memberId) => ({ event_id: eventId, member_id: memberId })))
    if (error) {
      return {
        ok: false,
        reason: dbError(error, 'Falha ao incluir participantes.'),
      }
    }
  }

  return { ok: true, data: next }
}

// --- Ocorrência de rotina ----------------------------------------------------

/**
 * Remarca ou cancela UMA ocorrência da série (SDD 09 R2).
 *
 * `originalDate` é a vaga na recorrência e é a chave do upsert: remarcar duas
 * vezes a mesma reunião atualiza a linha em vez de criar uma segunda. Sem essa
 * chave, o calendário acabaria mostrando a reunião nas duas datas novas.
 */
export async function upsertOccurrenceSlot(input: {
  routineId: string
  originalDate: string
  date: string
  status: Occurrence['status']
}): Promise<AgendaWriteResult<Occurrence>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('occurrence')
    .upsert(
      {
        routine_id: input.routineId,
        original_date: input.originalDate,
        date: input.date,
        status: input.status,
      },
      { onConflict: 'routine_id,original_date' },
    )
    .select('*')
    .single()

  if (error || !data) {
    return {
      ok: false,
      reason: dbError(error, 'Não foi possível remarcar a ocorrência.'),
    }
  }

  const row = data as {
    id: string
    routine_id: string
    date: string
    original_date: string
    status: Occurrence['status']
    agenda: string | null
    minutes: string | null
    attendance: Occurrence['attendance'] | null
  }
  return {
    ok: true,
    data: {
      id: row.id,
      routineId: row.routine_id,
      date: row.date,
      originalDate: row.original_date,
      status: row.status,
      attendance: row.attendance ?? {},
      ...(row.agenda ? { agenda: row.agenda } : {}),
      ...(row.minutes ? { minutes: row.minutes } : {}),
    },
  }
}
